import React from 'react';
import { Button, Card, Form, InputNumber, Select, Space, Table } from 'antd';
import { PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { useSkillStudioLayout } from './SkillStudio';

export interface SkillStudioOption {
    value: string | number | undefined;
    label: React.ReactNode;
}

export interface SkillStudioEvalRunsSectionProps<T extends object = Record<string, unknown>> {
    selectedConfig?: string;
    configOptions: SkillStudioOption[];
    onConfigChange: (value?: string) => void;
    selectedTenant?: string;
    tenantOptions: SkillStudioOption[];
    onTenantChange: (value?: string) => void;
    selectedSet?: string;
    setOptions: SkillStudioOption[];
    onSetChange: (value?: string) => void;
    selectedAgent?: string;
    agentOptions: SkillStudioOption[];
    onAgentChange: (value?: string) => void;
    selectedQuestionIds: number[];
    questionOptions: SkillStudioOption[];
    onQuestionIdsChange: (value: number[]) => void;
    repeats?: number;
    onRepeatsChange: (value?: number) => void;
    triggering: boolean;
    onTrigger: () => void;
    loading: boolean;
    onRefresh: () => void;
    tableProps: TableProps<T>;
    footerContent?: React.ReactNode;
}

const SkillStudioEvalRunsSection = <T extends object = Record<string, unknown>>({
    selectedConfig,
    configOptions,
    onConfigChange,
    selectedTenant,
    tenantOptions,
    onTenantChange,
    selectedSet,
    setOptions,
    onSetChange,
    selectedAgent,
    agentOptions,
    onAgentChange,
    selectedQuestionIds,
    questionOptions,
    onQuestionIdsChange,
    repeats,
    onRepeatsChange,
    triggering,
    onTrigger,
    loading,
    onRefresh,
    tableProps,
    footerContent,
}: SkillStudioEvalRunsSectionProps<T>) => {
    const skillStudioLayout = useSkillStudioLayout();
    const sectionHeight = skillStudioLayout != null ? '100%' : 'calc(100vh - 160px)';

    return (
        <div
            style={{
                overflow: 'auto',
                height: skillStudioLayout != null ? sectionHeight : undefined,
                maxHeight: skillStudioLayout != null ? undefined : sectionHeight,
                paddingBottom: 24,
                minHeight: 0,
            }}
        >
            <Card size="small" style={{ marginBottom: 16 }}>
                <Form layout="inline" style={{ flexWrap: 'wrap', gap: 8 }}>
                    <Form.Item label="Config" required>
                        <Select
                            placeholder="Select config"
                            style={{ width: 220 }}
                            value={selectedConfig}
                            onChange={onConfigChange}
                            options={configOptions}
                        />
                    </Form.Item>
                    <Form.Item label="Branch">
                        <Select
                            allowClear
                            placeholder="Default published branch"
                            style={{ width: 240 }}
                            value={selectedTenant}
                            onChange={onTenantChange}
                            options={tenantOptions}
                            showSearch
                        />
                    </Form.Item>
                    <Form.Item label="Question Set">
                        <Select
                            allowClear
                            placeholder="All approved"
                            style={{ width: 220 }}
                            value={selectedSet}
                            onChange={onSetChange}
                            options={setOptions}
                        />
                    </Form.Item>
                    <Form.Item label="Target Agent">
                        <Select
                            allowClear
                            placeholder="Full pipeline"
                            style={{ width: 180 }}
                            value={selectedAgent}
                            onChange={onAgentChange}
                            options={agentOptions}
                        />
                    </Form.Item>
                    {questionOptions.length > 0 && (
                        <Form.Item label="Questions" style={{ flex: '1 1 300px' }}>
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="All approved questions"
                                style={{ width: '100%', minWidth: 260 }}
                                value={selectedQuestionIds}
                                onChange={onQuestionIdsChange}
                                maxTagCount="responsive"
                                optionFilterProp="label"
                                options={questionOptions}
                            />
                        </Form.Item>
                    )}
                    <Form.Item label="Repeats">
                        <InputNumber
                            min={2}
                            placeholder="—"
                            style={{ width: 80 }}
                            value={repeats}
                            onChange={(v) => onRepeatsChange(v ?? undefined)}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            loading={triggering}
                            onClick={onTrigger}
                        >
                            Run Evaluation
                        </Button>
                    </Form.Item>
                </Form>
            </Card>

            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={loading} size="small">
                        Refresh
                    </Button>
                </Space>
            </div>

            <Table {...tableProps} />
            {footerContent}
        </div>
    );
};

export default SkillStudioEvalRunsSection;
